/**
 * Cleanup Utility for Orphaned Trip Data
 *
 * This utility helps clean up orphaned sub-collections and Storage files
 * from trips that were deleted using the old incomplete deleteTrip() function.
 *
 * Usage:
 *   import { cleanupOrphanedTripData, findOrphanedTrips } from '@trip-planner-monorepo/shared';
 *
 *   // Find all trips with orphaned data
 *   const orphaned = await findOrphanedTrips(userId);
 *
 *   // Clean up specific trip
 *   await cleanupOrphanedTripData(userId, tripId);
 *
 *   // Clean up all orphaned data for user
 *   await cleanupAllOrphanedData(userId);
 */

import { db, storage } from './firebaseInit';
import { collection, doc, getDocs, writeBatch, getDoc } from 'firebase/firestore';
import { ref, deleteObject, listAll } from 'firebase/storage';

/**
 * Find trips that have orphaned sub-collections but no trip document
 *
 * Strategy: Scan user's trips collection looking for:
 * 1. Trips where document exists but has orphaned sub-collections (shouldn't happen with new code)
 * 2. Storage files in trips/{tripId}/ folders where trip document doesn't exist
 *
 * @param {string} userId - User ID to scan
 * @returns {Promise<Object>} Report of orphaned data found
 */
export const findOrphanedTrips = async (userId) => {
  console.log(`[Cleanup] Scanning for orphaned data for user: ${userId}`);

  const report = {
    orphanedTrips: [],
    orphanedStorageFiles: [],
    totalOrphanedSubCollections: 0,
    totalOrphanedFiles: 0
  };

  try {
    // Get all existing trip IDs
    const tripsRef = collection(db, 'users', userId, 'trips');
    const tripsSnapshot = await getDocs(tripsRef);
    const validTripIds = new Set(tripsSnapshot.docs.map(doc => doc.id));

    console.log(`[Cleanup] Found ${validTripIds.size} valid trips`);

    // Check each valid trip for orphaned sub-collections
    for (const tripDoc of tripsSnapshot.docs) {
      const tripId = tripDoc.id;
      const tripData = tripDoc.data();

      console.log(`[Cleanup] Checking trip: ${tripId} (${tripData.name || 'No name'})`);

      const subCollectionCounts = await countSubCollections(userId, tripId);
      const hasOrphanedData = Object.values(subCollectionCounts).some(count => count > 0);

      // Note: Valid trips shouldn't have orphaned data with new code
      // But we check anyway in case of past issues
      if (hasOrphanedData) {
        console.log(`[Cleanup] ⚠️  Trip ${tripId} has sub-collections (may be valid data)`);
      }
    }

    // Check Storage for orphaned document files
    console.log('[Cleanup] Scanning Firebase Storage for orphaned files...');

    const userStorageRef = ref(storage, `users/${userId}/trips`);
    try {
      const storageList = await listAll(userStorageRef);

      // Check each trip folder in storage
      for (const tripFolderRef of storageList.prefixes) {
        const tripId = tripFolderRef.name;

        // Check if trip exists in Firestore
        if (!validTripIds.has(tripId)) {
          console.log(`[Cleanup] ⚠️  Found orphaned storage folder: trips/${tripId}`);

          // List all files in this orphaned trip folder
          const documentsRef = ref(storage, `users/${userId}/trips/${tripId}/documents`);
          try {
            const docsList = await listAll(documentsRef);
            const fileCount = await countStorageFiles(docsList);

            report.orphanedStorageFiles.push({
              tripId,
              path: `users/${userId}/trips/${tripId}/documents`,
              fileCount
            });
            report.totalOrphanedFiles += fileCount;

            console.log(`[Cleanup]    Found ${fileCount} orphaned files in this trip`);
          } catch (error) {
            console.warn(`[Cleanup] Could not list files in ${tripId}/documents:`, error.message);
          }
        }
      }
    } catch (storageError) {
      console.warn('[Cleanup] Could not access Storage (may be empty or permissions issue):', storageError.message);
    }

    // Summary
    console.log('[Cleanup] Scan complete:');
    console.log(`[Cleanup]   Valid trips: ${validTripIds.size}`);
    console.log(`[Cleanup]   Orphaned storage folders: ${report.orphanedStorageFiles.length}`);
    console.log(`[Cleanup]   Total orphaned files: ${report.totalOrphanedFiles}`);

    return report;

  } catch (error) {
    console.error('[Cleanup] Error scanning for orphaned data:', error);
    throw error;
  }
};

/**
 * Count sub-collections for a trip
 *
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID
 * @returns {Promise<Object>} Count of documents in each sub-collection
 */
const countSubCollections = async (userId, tripId) => {
  const collections = ['days', 'places', 'accommodation', 'travel', 'documents'];
  const counts = {};

  for (const collectionName of collections) {
    try {
      const collectionRef = collection(db, 'users', userId, 'trips', tripId, collectionName);
      const snapshot = await getDocs(collectionRef);
      counts[collectionName] = snapshot.size;
    } catch (error) {
      counts[collectionName] = 0;
    }
  }

  return counts;
};

/**
 * Recursively count files in storage location
 *
 * @param {Object} listResult - Result from listAll()
 * @returns {Promise<number>} Total file count
 */
const countStorageFiles = async (listResult) => {
  let count = listResult.items.length;

  // Recursively count files in subdirectories
  for (const prefix of listResult.prefixes) {
    const subList = await listAll(prefix);
    count += await countStorageFiles(subList);
  }

  return count;
};

/**
 * Clean up orphaned data for a specific trip ID
 * This handles the case where trip document was deleted but sub-collections remain
 *
 * @param {string} userId - User ID
 * @param {string} tripId - Trip ID to clean up
 * @returns {Promise<Object>} Cleanup statistics
 */
export const cleanupOrphanedTripData = async (userId, tripId) => {
  console.log(`[Cleanup] Starting cleanup for trip: ${tripId}`);

  const stats = {
    subCollectionsDeleted: 0,
    storageFilesDeleted: 0,
    errors: []
  };

  try {
    // Check if trip document exists
    const tripRef = doc(db, 'users', userId, 'trips', tripId);
    const tripDoc = await getDoc(tripRef);

    if (tripDoc.exists()) {
      console.log(`[Cleanup] ⚠️  WARNING: Trip ${tripId} still exists in Firestore!`);
      console.log('[Cleanup] Use deleteTrip() instead of this cleanup utility.');
      throw new Error('Cannot cleanup active trip - use deleteTrip() instead');
    }

    // Step 1: Delete orphaned sub-collections
    console.log('[Cleanup] Step 1: Deleting orphaned sub-collections...');
    const batch = writeBatch(db);
    const collections = ['days', 'places', 'accommodation', 'travel', 'documents'];

    for (const collectionName of collections) {
      const collectionRef = collection(db, 'users', userId, 'trips', tripId, collectionName);
      const snapshot = await getDocs(collectionRef);

      snapshot.forEach(docSnap => {
        batch.delete(docSnap.ref);
        stats.subCollectionsDeleted++;
      });

      console.log(`[Cleanup] Queued ${snapshot.size} documents from ${collectionName}`);
    }

    await batch.commit();
    console.log(`[Cleanup] Deleted ${stats.subCollectionsDeleted} sub-collection documents`);

    // Step 2: Delete orphaned Storage files
    console.log('[Cleanup] Step 2: Deleting orphaned Storage files...');
    const tripStorageRef = ref(storage, `users/${userId}/trips/${tripId}`);

    try {
      await deleteStorageFolder(tripStorageRef, stats);
      console.log(`[Cleanup] Deleted ${stats.storageFilesDeleted} storage files`);
    } catch (storageError) {
      console.warn('[Cleanup] Storage deletion encountered errors:', storageError.message);
      stats.errors.push(`Storage: ${storageError.message}`);
    }

    console.log(`[Cleanup] Cleanup complete for trip ${tripId}`);
    console.log('[Cleanup] Stats:', stats);

    return stats;

  } catch (error) {
    console.error('[Cleanup] Error during cleanup:', error);
    throw error;
  }
};

/**
 * Recursively delete a storage folder and all its contents
 *
 * @param {Object} folderRef - Firebase Storage reference to folder
 * @param {Object} stats - Statistics object to update
 */
const deleteStorageFolder = async (folderRef, stats) => {
  try {
    const listResult = await listAll(folderRef);

    // Delete all files in this folder
    for (const fileRef of listResult.items) {
      try {
        await deleteObject(fileRef);
        stats.storageFilesDeleted++;
        console.log(`[Cleanup] Deleted: ${fileRef.fullPath}`);
      } catch (error) {
        console.warn(`[Cleanup] Could not delete ${fileRef.fullPath}:`, error.message);
        stats.errors.push(`File: ${fileRef.fullPath} - ${error.message}`);
      }
    }

    // Recursively delete subdirectories
    for (const subFolderRef of listResult.prefixes) {
      await deleteStorageFolder(subFolderRef, stats);
    }
  } catch (error) {
    console.warn(`[Cleanup] Error listing folder ${folderRef.fullPath}:`, error.message);
    throw error;
  }
};

/**
 * Clean up ALL orphaned data for a user
 * WARNING: This scans and cleans up all orphaned data - use with caution
 *
 * @param {string} userId - User ID
 * @param {boolean} dryRun - If true, only report what would be deleted (default: true)
 * @returns {Promise<Object>} Cleanup report
 */
export const cleanupAllOrphanedData = async (userId, dryRun = true) => {
  console.log(`[Cleanup] ${dryRun ? 'DRY RUN:' : ''} Cleaning up all orphaned data for user: ${userId}`);

  // First, find all orphaned data
  const report = await findOrphanedTrips(userId);

  if (dryRun) {
    console.log('[Cleanup] DRY RUN complete - no data was deleted');
    console.log('[Cleanup] To actually delete, call with dryRun=false');
    return {
      ...report,
      dryRun: true,
      message: 'Dry run complete - no data deleted'
    };
  }

  // Actually clean up orphaned storage folders
  const cleanupStats = {
    tripsProcessed: 0,
    totalSubCollectionsDeleted: 0,
    totalStorageFilesDeleted: 0,
    errors: []
  };

  for (const orphanedFolder of report.orphanedStorageFiles) {
    try {
      console.log(`[Cleanup] Cleaning up orphaned trip: ${orphanedFolder.tripId}`);
      const stats = await cleanupOrphanedTripData(userId, orphanedFolder.tripId);

      cleanupStats.tripsProcessed++;
      cleanupStats.totalSubCollectionsDeleted += stats.subCollectionsDeleted;
      cleanupStats.totalStorageFilesDeleted += stats.storageFilesDeleted;
      cleanupStats.errors.push(...stats.errors);

    } catch (error) {
      console.error(`[Cleanup] Failed to cleanup trip ${orphanedFolder.tripId}:`, error);
      cleanupStats.errors.push(`Trip ${orphanedFolder.tripId}: ${error.message}`);
    }
  }

  console.log('[Cleanup] All orphaned data cleaned up');
  console.log('[Cleanup] Final stats:', cleanupStats);

  return {
    ...report,
    ...cleanupStats,
    dryRun: false,
    message: 'Cleanup complete'
  };
};

/**
 * Generate a cleanup report for display to user
 *
 * @param {string} userId - User ID
 * @returns {Promise<string>} Formatted report
 */
export const generateCleanupReport = async (userId) => {
  const report = await findOrphanedTrips(userId);

  let output = '\n=== ORPHANED DATA CLEANUP REPORT ===\n\n';
  output += `User ID: ${userId}\n`;
  output += `Scan Date: ${new Date().toLocaleString()}\n\n`;

  output += '--- Orphaned Storage Folders ---\n';
  if (report.orphanedStorageFiles.length === 0) {
    output += '✅ No orphaned storage folders found\n';
  } else {
    output += `⚠️  Found ${report.orphanedStorageFiles.length} orphaned storage folders:\n\n`;
    report.orphanedStorageFiles.forEach(folder => {
      output += `  Trip ID: ${folder.tripId}\n`;
      output += `  Path: ${folder.path}\n`;
      output += `  Files: ${folder.fileCount}\n\n`;
    });
  }

  output += '\n--- Summary ---\n';
  output += `Total Orphaned Files: ${report.totalOrphanedFiles}\n`;

  if (report.totalOrphanedFiles > 0) {
    output += '\n⚠️  ACTION REQUIRED: Run cleanup to remove orphaned data\n';
    output += 'Usage: cleanupAllOrphanedData(userId, false)\n';
  } else {
    output += '\n✅ No cleanup needed - all data is properly organized\n';
  }

  output += '\n====================================\n';

  return output;
};
